import { useState, useEffect } from 'react';
import { X, SlidersHorizontal, RotateCcw, Check, Bell } from 'lucide-react';
import { api } from '../lib/api.jsx';

const inputCls =
  'bg-sidebar border border-rule rounded-lg px-2.5 py-1.5 text-fg text-xs font-mono outline-none w-[80px] text-right';

const Field = ({ label, hint, value, onChange, unit = '%', min = 1, max = 100 }) => (
  <div className="flex items-center justify-between gap-3 py-1.5 border-b border-rule last:border-b-0">
    <div>
      <div className="text-[13px] text-fg-2 font-semibold">{label}</div>
      {hint && <div className="text-[10.5px] text-muted-2 mt-0.5">{hint}</div>}
    </div>
    <div className="flex items-center gap-1">
      <input
        type="number"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className={inputCls}
      />
      <span className="text-muted-2 text-xs">{unit}</span>
    </div>
  </div>
);

export function AlertThresholdsModal({ agentId, computerName, canEdit, onClose }) {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    api.get('/platform/config/alerts/agent/' + agentId).then((r) => {
      if (!active) return;
      if (!r) {
        setError('Impossible de charger les seuils.');
        setLoading(false);
        return;
      }
      setData(r);
      setDraft({
        memoryWarningPercent: r.effective.memoryWarningPercent,
        memoryCriticalPercent: r.effective.memoryCriticalPercent,
        diskWarningPercent: r.effective.diskWarningPercent,
        diskCriticalPercent: r.effective.diskCriticalPercent,
        cpuWarningPercent: r.effective.cpuWarningPercent ?? 90,
        cpuCriticalPercent: r.effective.cpuCriticalPercent ?? 97,
        cpuSustainedMinutes: r.effective.cpuSustainedMinutes ?? 5,
        serverOfflineAlertHours: r.effective.serverOfflineAlertHours ?? 1,
        gpuWarningPercent: r.effective.gpuWarningPercent ?? 85,
        gpuCriticalPercent: r.effective.gpuCriticalPercent ?? 98,
        vRamWarningPercent: r.effective.vRamWarningPercent ?? 85,
        vRamCriticalPercent: r.effective.vRamCriticalPercent ?? 95,
        cpuTempWarningC: r.effective.cpuTempWarningC ?? 85,
        cpuTempCriticalC: r.effective.cpuTempCriticalC ?? 95,
        gpuTempWarningC: r.effective.gpuTempWarningC ?? 80,
        gpuTempCriticalC: r.effective.gpuTempCriticalC ?? 90,
        alertOnThermalThrottle: r.effective.alertOnThermalThrottle ?? true,
        alertOnPendingUpdates: r.effective.alertOnPendingUpdates,
        alertOnContainerIssues: r.effective.alertOnContainerIssues ?? true,
        alertOnDiskHealth: r.effective.alertOnDiskHealth ?? true,
        diskWearWarningPercent: r.effective.diskWearWarningPercent ?? 90,
        diskWearCriticalPercent: r.effective.diskWearCriticalPercent ?? 95,
        diskTempWarningC: r.effective.diskTempWarningC ?? 55,
        diskTempCriticalC: r.effective.diskTempCriticalC ?? 65,
        diskReallocatedWarning: r.effective.diskReallocatedWarning ?? 10,
        diskPendingSectorWarning: r.effective.diskPendingSectorWarning ?? 1,
      });
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [agentId]);

  const validate = (d) => {
    if (!d) return 'Champs invalides';
    for (const k of [
      'memoryWarningPercent',
      'memoryCriticalPercent',
      'diskWarningPercent',
      'diskCriticalPercent',
      'cpuWarningPercent',
      'cpuCriticalPercent',
      'gpuWarningPercent',
      'gpuCriticalPercent',
      'vRamWarningPercent',
      'vRamCriticalPercent',
    ]) {
      const v = Number(d[k]);
      if (!Number.isFinite(v) || v < 1 || v > 100) return 'Les seuils % doivent être entre 1 et 100.';
    }
    for (const k of ['cpuTempWarningC', 'cpuTempCriticalC', 'gpuTempWarningC', 'gpuTempCriticalC']) {
      const v = Number(d[k]);
      if (!Number.isFinite(v) || v < 30 || v > 130)
        return 'Les seuils de température doivent être entre 30°C et 130°C.';
    }
    if (d.memoryWarningPercent >= d.memoryCriticalPercent)
      return "Mémoire : avertissement doit être < critique.";
    if (d.diskWarningPercent >= d.diskCriticalPercent) return "Disque : avertissement doit être < critique.";
    if (d.cpuWarningPercent >= d.cpuCriticalPercent) return "CPU : avertissement doit être < critique.";
    const cpuWindow = Number(d.cpuSustainedMinutes);
    if (!Number.isInteger(cpuWindow) || cpuWindow < 0 || cpuWindow > 60)
      return 'La fenêtre CPU doit être un entier entre 0 (désactivé) et 60 minutes.';
    const offlineHours = Number(d.serverOfflineAlertHours);
    if (!Number.isInteger(offlineHours) || offlineHours < 0 || offlineHours > 168)
      return 'Le délai « serveur hors ligne » doit être un entier entre 0 (désactivé) et 168 heures.';
    if (d.gpuWarningPercent >= d.gpuCriticalPercent) return "GPU : avertissement doit être < critique.";
    if (d.vRamWarningPercent >= d.vRamCriticalPercent) return "VRAM : avertissement doit être < critique.";
    if (d.cpuTempWarningC >= d.cpuTempCriticalC) return "T° CPU : avertissement doit être < critique.";
    if (d.gpuTempWarningC >= d.gpuTempCriticalC) return "T° GPU : avertissement doit être < critique.";
    // Plage propre aux disques : un disque tourne bien plus froid qu'un CPU, et
    // 30 °C serait déjà atteint au repos dans un boîtier fermé.
    for (const k of ['diskTempWarningC', 'diskTempCriticalC']) {
      const v = Number(d[k]);
      if (!Number.isFinite(v) || v < 25 || v > 95)
        return 'Les seuils de température disque doivent être entre 25°C et 95°C.';
    }
    if (d.diskTempWarningC >= d.diskTempCriticalC) return "T° disque : avertissement doit être < critique.";
    for (const k of ['diskWearWarningPercent', 'diskWearCriticalPercent']) {
      const v = Number(d[k]);
      if (!Number.isFinite(v) || v < 1 || v > 100) return "Usure disque : les seuils doivent être entre 1 et 100 %.";
    }
    if (d.diskWearWarningPercent >= d.diskWearCriticalPercent)
      return "Usure disque : avertissement doit être < critique.";
    for (const k of ['diskReallocatedWarning', 'diskPendingSectorWarning']) {
      const v = Number(d[k]);
      if (!Number.isInteger(v) || v < 1) return 'Les seuils de secteurs doivent être des entiers ≥ 1.';
    }
    return null;
  };

  const save = async () => {
    const err = validate(draft);
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    setSaving(true);
    const r = await api.fetch('/platform/config/alerts/agent/' + agentId, {
      method: 'PUT',
      body: JSON.stringify(draft),
    });
    setSaving(false);
    if (r.ok) {
      onClose(true);
    } else {
      const t = await r.text().catch(() => '');
      try {
        const j = JSON.parse(t);
        setError(j.error || 'Erreur lors de la sauvegarde.');
      } catch {
        setError('Erreur lors de la sauvegarde.');
      }
    }
  };

  const resetToInherited = async () => {
    if (!data?.hasOverride) return;
    if (!confirm('Supprimer la surcharge per-poste et revenir aux seuils globaux ?')) return;
    setSaving(true);
    const ok = await api.del('/platform/config/alerts/agent/' + agentId);
    setSaving(false);
    if (ok) onClose(true);
    else setError('Erreur lors de la suppression de la surcharge.');
  };

  return (
    // La liste de seuils dépasse la hauteur d'écran sur un portable : le contenu
    // défile, mais l'en-tête et les boutons restent visibles (sinon « Enregistrer »
    // se retrouve hors écran, sans moyen de l'atteindre). `min-h-0` sur le corps est
    // indispensable — sans lui un enfant flex refuse de rétrécir et rien ne défile.
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[1000] p-4">
      <div className="bg-card border border-rule rounded-2xl w-[920px] max-w-full max-h-[90vh] shadow-2xl flex flex-col overflow-hidden">
        <div className="px-6 pt-6 flex-none">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <SlidersHorizontal size={18} strokeWidth={1.75} className="text-blue-400" />
            <h2 className="text-base font-bold text-fg">Seuils d'alertes</h2>
          </div>
          <button
            onClick={() => onClose(false)}
            className="bg-transparent border-none text-muted cursor-pointer hover:text-fg p-1"
          >
            <X size={18} strokeWidth={1.75} />
          </button>
        </div>

        <div className="text-[12px] text-muted-2 mb-3">
          {computerName ? <span className="text-fg-2 font-semibold">{computerName}</span> : 'Cet appareil'}
          {data && (
            <span className="ml-2 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide border bg-sidebar border-rule text-muted">
              Source : {data.source === 'agent' ? 'Per-poste' : data.source === 'site' ? 'Site' : 'Global'}
            </span>
          )}
        </div>
        </div>

        {loading ? (
          <div className="text-muted-2 text-sm py-6 px-6 text-center">Chargement…</div>
        ) : !draft ? (
          <div className="text-red-300 text-sm py-6 px-6 text-center">{error || 'Erreur'}</div>
        ) : (
          <>
            <div className="px-6 pb-2 overflow-y-auto flex-1 min-h-0">
            {/* Deux colonnes dès 768 px : la liste de seuils est longue et
                une seule colonne obligeait à défiler pour atteindre les
                cases à cocher du bas. `items-start` : une carte courte
                (case à cocher) ne s'étire pas à la hauteur de sa voisine. */}
            <div className="md:columns-2 gap-3">
            <div className="bg-sidebar border border-rule rounded-xl px-3.5 py-2 break-inside-avoid mb-3">
              <div className="text-[11px] uppercase tracking-widest text-blue-400 font-bold mb-1 mt-1">
                Mémoire
              </div>
              <Field
                label="Avertissement"
                hint="Au-dessus de ce seuil, alerte de niveau Warning"
                value={draft.memoryWarningPercent}
                onChange={(v) => setDraft({ ...draft, memoryWarningPercent: v })}
              />
              <Field
                label="Critique"
                hint="Au-dessus de ce seuil, alerte de niveau Critical"
                value={draft.memoryCriticalPercent}
                onChange={(v) => setDraft({ ...draft, memoryCriticalPercent: v })}
              />
            </div>

            <div className="bg-sidebar border border-rule rounded-xl px-3.5 py-2 break-inside-avoid mb-3">
              <div className="text-[11px] uppercase tracking-widest text-blue-400 font-bold mb-1 mt-1">
                Espace disque
              </div>
              <Field
                label="Avertissement"
                hint="S'applique à chaque disque"
                value={draft.diskWarningPercent}
                onChange={(v) => setDraft({ ...draft, diskWarningPercent: v })}
              />
              <Field
                label="Critique"
                value={draft.diskCriticalPercent}
                onChange={(v) => setDraft({ ...draft, diskCriticalPercent: v })}
              />
            </div>

            <div className="bg-sidebar border border-rule rounded-xl px-3.5 py-2 break-inside-avoid mb-3">
              <div className="text-[11px] uppercase tracking-widest text-blue-400 font-bold mb-1 mt-1">
                CPU soutenu
              </div>
              <Field
                label="Avertissement"
                hint="Sur la MOYENNE de la fenêtre, pas sur un pic"
                value={draft.cpuWarningPercent}
                onChange={(v) => setDraft({ ...draft, cpuWarningPercent: v })}
              />
              <Field
                label="Critique"
                value={draft.cpuCriticalPercent}
                onChange={(v) => setDraft({ ...draft, cpuCriticalPercent: v })}
              />
              <Field
                label="Fenêtre d'évaluation"
                hint="0 désactive l'alerte CPU. Une pointe à 100 % de quelques secondes est normale — c'est la durée qui fait l'incident."
                value={draft.cpuSustainedMinutes}
                onChange={(v) => setDraft({ ...draft, cpuSustainedMinutes: v })}
                unit="min"
                min={0}
                max={60}
              />
            </div>

            <div className="bg-sidebar border border-rule rounded-xl px-3.5 py-2 break-inside-avoid mb-3">
              <div className="text-[11px] uppercase tracking-widest text-blue-400 font-bold mb-1 mt-1">
                Connectivité (serveurs)
              </div>
              <Field
                label="Serveur hors ligne : alerter après"
                hint="0 = désactivé. Ne concerne que les serveurs ; le décompte repart à la fin d'une fenêtre de maintenance."
                value={draft.serverOfflineAlertHours}
                onChange={(v) => setDraft({ ...draft, serverOfflineAlertHours: v })}
                unit="h"
                min={0}
                max={168}
              />
            </div>

            <div className="bg-sidebar border border-rule rounded-xl px-3.5 py-2 break-inside-avoid mb-3">
              <div className="text-[11px] uppercase tracking-widest text-blue-400 font-bold mb-1 mt-1">
                GPU
              </div>
              <Field
                label="Avertissement"
                hint="Ignoré si l'appareil n'a pas de GPU monitoré"
                value={draft.gpuWarningPercent}
                onChange={(v) => setDraft({ ...draft, gpuWarningPercent: v })}
              />
              <Field
                label="Critique"
                value={draft.gpuCriticalPercent}
                onChange={(v) => setDraft({ ...draft, gpuCriticalPercent: v })}
              />
            </div>

            <div className="bg-sidebar border border-rule rounded-xl px-3.5 py-2 break-inside-avoid mb-3">
              <div className="text-[11px] uppercase tracking-widest text-blue-400 font-bold mb-1 mt-1">
                Mémoire vidéo (VRAM)
              </div>
              <Field
                label="Avertissement"
                value={draft.vRamWarningPercent}
                onChange={(v) => setDraft({ ...draft, vRamWarningPercent: v })}
              />
              <Field
                label="Critique"
                value={draft.vRamCriticalPercent}
                onChange={(v) => setDraft({ ...draft, vRamCriticalPercent: v })}
              />
            </div>

            <div className="bg-sidebar border border-rule rounded-xl px-3.5 py-2 break-inside-avoid mb-3">
              <div className="text-[11px] uppercase tracking-widest text-blue-400 font-bold mb-1 mt-1">
                Température CPU
              </div>
              <Field
                label="Avertissement"
                hint="Ignoré si aucun capteur thermique n'est disponible"
                value={draft.cpuTempWarningC}
                onChange={(v) => setDraft({ ...draft, cpuTempWarningC: v })}
                unit="°C"
                min={30}
                max={130}
              />
              <Field
                label="Critique"
                value={draft.cpuTempCriticalC}
                onChange={(v) => setDraft({ ...draft, cpuTempCriticalC: v })}
                unit="°C"
                min={30}
                max={130}
              />
            </div>

            <div className="bg-sidebar border border-rule rounded-xl px-3.5 py-2 break-inside-avoid mb-3">
              <div className="text-[11px] uppercase tracking-widest text-blue-400 font-bold mb-1 mt-1">
                Température GPU
              </div>
              <Field
                label="Avertissement"
                value={draft.gpuTempWarningC}
                onChange={(v) => setDraft({ ...draft, gpuTempWarningC: v })}
                unit="°C"
                min={30}
                max={130}
              />
              <Field
                label="Critique"
                value={draft.gpuTempCriticalC}
                onChange={(v) => setDraft({ ...draft, gpuTempCriticalC: v })}
                unit="°C"
                min={30}
                max={130}
              />
            </div>

            <div className="bg-sidebar border border-rule rounded-xl px-3.5 py-2 break-inside-avoid mb-3">
              <div className="text-[11px] uppercase tracking-widest text-blue-400 font-bold mb-1 mt-1">
                Santé disque (S.M.A.R.T.)
              </div>
              <Field
                label="Usure — avertissement"
                hint="Pourcentage d'endurance consommée, SSD et NVMe seulement"
                value={draft.diskWearWarningPercent}
                onChange={(v) => setDraft({ ...draft, diskWearWarningPercent: v })}
              />
              <Field
                label="Usure — critique"
                value={draft.diskWearCriticalPercent}
                onChange={(v) => setDraft({ ...draft, diskWearCriticalPercent: v })}
              />
              <Field
                label="Température — avertissement"
                value={draft.diskTempWarningC}
                onChange={(v) => setDraft({ ...draft, diskTempWarningC: v })}
                unit="°C"
                min={25}
                max={95}
              />
              <Field
                label="Température — critique"
                value={draft.diskTempCriticalC}
                onChange={(v) => setDraft({ ...draft, diskTempCriticalC: v })}
                unit="°C"
                min={25}
                max={95}
              />
              <Field
                label="Secteurs réalloués"
                hint="Compteur cumulé : il ne redescend jamais, d'où un seuil > 1"
                value={draft.diskReallocatedWarning}
                onChange={(v) => setDraft({ ...draft, diskReallocatedWarning: v })}
                unit=""
                min={1}
                max={100000}
              />
              <Field
                label="Secteurs en attente"
                hint="Secteurs instables — le signal le plus prédictif d'un disque qui part"
                value={draft.diskPendingSectorWarning}
                onChange={(v) => setDraft({ ...draft, diskPendingSectorWarning: v })}
                unit=""
                min={1}
                max={100000}
              />
            </div>

            <div className="bg-sidebar border border-rule rounded-xl px-3.5 pt-3.5 pb-2.5 break-inside-avoid mt-1 mb-3 [column-span:all]">
              <div className="text-[11px] uppercase tracking-widest text-blue-400 font-bold mb-2">
                Contrôles activés
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
            <label className="flex items-center gap-2.5 py-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={!!draft.alertOnThermalThrottle}
                onChange={(e) => setDraft({ ...draft, alertOnThermalThrottle: e.target.checked })}
                className="accent-blue-500 cursor-pointer"
              />
              <Bell size={14} strokeWidth={1.75} className="text-muted-2" />
              <span className="text-[13px] text-fg-2">Alerter quand le matériel signale du thermal throttling</span>
            </label>

            <label className="flex items-center gap-2.5 py-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={!!draft.alertOnPendingUpdates}
                onChange={(e) => setDraft({ ...draft, alertOnPendingUpdates: e.target.checked })}
                className="accent-blue-500 cursor-pointer"
              />
              <Bell size={14} strokeWidth={1.75} className="text-muted-2" />
              <span className="text-[13px] text-fg-2">
                Alerter sur les mises à jour en attente (KB Windows ou paquets apt/dnf)
              </span>
            </label>

            <label className="flex items-center gap-2.5 py-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={!!draft.alertOnDiskHealth}
                onChange={(e) => setDraft({ ...draft, alertOnDiskHealth: e.target.checked })}
                className="accent-blue-500 cursor-pointer"
              />
              <Bell size={14} strokeWidth={1.75} className="text-muted-2" />
              <span className="text-[13px] text-fg-2">
                Alerter sur la santé S.M.A.R.T. des disques (défaillance annoncée, usure, secteurs)
              </span>
            </label>

            <label className="flex items-center gap-2.5 py-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={!!draft.alertOnContainerIssues}
                onChange={(e) => setDraft({ ...draft, alertOnContainerIssues: e.target.checked })}
                className="accent-blue-500 cursor-pointer"
              />
              <Bell size={14} strokeWidth={1.75} className="text-muted-2" />
              <span className="text-[13px] text-fg-2">
                Alerter sur les conteneurs en mauvaise santé ou en boucle de redémarrage
              </span>
            </label>

              </div>
            </div>
            </div>
            {error && (
              <div className="bg-crit/12 border border-crit/30 rounded-lg px-3 py-2 mb-3 text-red-300 text-xs">
                {error}
              </div>
            )}
            </div>

            <div className="px-6 pb-6 pt-4 flex-none border-t border-rule">
            <div className="flex justify-between items-center">
              <button
                onClick={resetToInherited}
                disabled={!canEdit || !data?.hasOverride || saving}
                className="bg-transparent border border-rule-3 rounded-lg px-3 py-2 text-muted text-xs font-semibold cursor-pointer disabled:opacity-40 disabled:cursor-default inline-flex items-center gap-1.5"
                title={
                  data?.hasOverride
                    ? 'Supprimer la surcharge per-poste'
                    : 'Aucune surcharge per-poste à supprimer'
                }
              >
                <RotateCcw size={13} strokeWidth={1.75} /> Hériter du global
              </button>
              <div className="flex gap-2">
                <button
                  onClick={() => onClose(false)}
                  className="bg-rule border border-rule-3 rounded-lg px-4 py-2 text-muted text-[13px] font-semibold cursor-pointer"
                >
                  Annuler
                </button>
                <button
                  onClick={save}
                  disabled={!canEdit || saving}
                  className="bg-blue-500 border-none rounded-lg px-4 py-2 text-white text-[13px] font-semibold cursor-pointer disabled:opacity-60 inline-flex items-center gap-1.5 hover:bg-blue-600"
                >
                  {saving ? (
                    'Enregistrement…'
                  ) : (
                    <>
                      <Check size={14} strokeWidth={2} /> Enregistrer
                    </>
                  )}
                </button>
              </div>
            </div>

            {!canEdit && (
              <div className="text-[11px] text-muted-2 mt-3 text-right">
                Lecture seule — permission « ManageConfig » requise pour modifier.
              </div>
            )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
